/**
 * HR assistant.
 *
 * Retrieval-grounded rather than generative: each answer is written against a
 * known policy and cites the version it came from, and the figures inside it are
 * pulled from this employee's live records rather than restated from memory. When
 * nothing matches confidently it says so and offers a ticket, because a plausible
 * wrong answer about someone's leave balance is worse than no answer.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { Icon } from '../lib/icons.jsx';
import { SUGGESTIONS, findAnswer } from '../lib/assistant.js';

export default function Assistant({ open, onOpen, onClose, onRaiseTicket }) {
  const { user, can } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const bodyRef = useRef(null);
  const areaRef = useRef(null);

  // Only fetched once the panel is opened — the assistant should not cost every
  // page load four extra requests for something most people never open.
  const balances = useQuery({ queryKey: ['leave', 'balances', user.id], queryFn: () => get(`/leave/balances/${user.id}`), enabled: open });
  const payslips = useQuery({ queryKey: ['payslips', 'me'], queryFn: () => get('/payroll/payslips/me'), enabled: open && can('payroll.read.self') });
  const holidays = useQuery({ queryKey: ['holidays', 'upcoming'], queryFn: () => get('/reference/holidays/upcoming'), enabled: open, staleTime: 300_000 });
  const devices = useQuery({ queryKey: ['devices'], queryFn: () => get('/biometric/devices'), enabled: open && can('device.read') });

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, thinking, open]);

  const send = (text) => {
    const question = (text ?? '').trim();
    if (!question) return;
    setMessages((m) => [...m, { role: 'me', text: question }]);
    setDraft('');
    setThinking(true);
    // A beat of latency: an answer that lands instantly reads as canned.
    setTimeout(() => {
      const result = findAnswer(question, {
        balances: balances.data?.data,
        payslips: payslips.data?.data,
        holidays: holidays.data?.data,
        devices: devices.data?.data,
      });
      setThinking(false);
      setMessages((m) => [...m, { role: 'bot', ...result }]);
    }, 520);
  };

  if (!open) {
    return (
      <button type="button" className="ai-fab" onClick={onOpen} title="AI HR assistant" aria-label="Open HR assistant">
        <Icon name="sparkles" />
      </button>
    );
  }

  return (
    <div className="ai-panel" role="dialog" aria-label="HR assistant">
      <div className="ai-head">
        <div className="ai-orb"><Icon name="sparkles" /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13.8 }}>HR Assistant</div>
          <div className="txt-sm muted">Grounded in SevenMentor policies · {user.firstName}'s view</div>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close assistant"><Icon name="x" /></button>
      </div>

      <div className="ai-body" ref={bodyRef}>
        {messages.length === 0 && (
          <div className="msg bot">
            Hello {user.firstName} — I can answer questions about leave, attendance, payroll, policies and
            benefits using SevenMentor&apos;s own documents.
            <br /><br />
            I will always tell you which policy an answer came from, and if I do not know something I will
            say so and offer to raise a ticket instead of guessing.
          </div>
        )}

        {messages.map((m, i) => (m.role === 'me'
          ? <div className="msg me" key={i}>{m.text}</div>
          : (
            <div className="msg bot" key={i}>
              <span dangerouslySetInnerHTML={{ __html: m.html }} />
              {m.source && (
                <div className="ai-src"><Icon name="book" /> Source: {m.source}</div>
              )}
              {m.offerTicket && (
                <div style={{ marginTop: 9 }}>
                  <button type="button" className="btn btn-xs btn-primary" onClick={onRaiseTicket}>
                    <Icon name="ticket" />Raise a ticket
                  </button>
                </div>
              )}
            </div>
          )))}

        {thinking && <div className="msg bot typing"><i /><i /><i /></div>}
      </div>

      <div className="ai-foot">
        <div className="ai-sugg">
          {SUGGESTIONS.slice(0, messages.length ? 3 : 6).map((s) => (
            <button key={s} type="button" onClick={() => send(s)}>{s}</button>
          ))}
        </div>
        <div className="ai-input">
          <textarea
            ref={areaRef}
            rows={1}
            value={draft}
            placeholder="Ask about leave, payroll, policy…"
            onChange={(e) => {
              setDraft(e.target.value);
              const el = areaRef.current;
              if (el) { el.style.height = '40px'; el.style.height = `${Math.min(96, el.scrollHeight)}px`; }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft); }
            }}
          />
          <button type="button" className="ai-send" onClick={() => send(draft)} aria-label="Send">
            <Icon name="send" />
          </button>
        </div>
      </div>
    </div>
  );
}
